import { baseBackendUrl } from './baseService';
import { GroupApiError } from './GroupService';
import {
    AcceptGroupInvitationRequest,
    AcceptGroupInvitationResponse,
    CreateGroupInvitationRequest,
    GroupInvitation,
    GroupInvitationSetup,
} from '../types/group';

const invitationsUrl = `${baseBackendUrl}/api/group-invitations`;

const buildHeaders = (token: string, json = false): HeadersInit => {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
};

// Like GroupService's send, but a failed request carries the server's response text
// (e.g. a 400 validation message) so the page can show it; falls back to errorMessage.
const send = async (
    path: string,
    errorMessage: string,
    token: string,
    method: string = 'GET',
    body?: unknown
): Promise<Response> => {
    const response = await fetch(`${invitationsUrl}${path}`, {
        method,
        headers: buildHeaders(token, body !== undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
        let message = errorMessage;
        try {
            const text = (await response.text()).trim();
            // Plain-text bodies only; ProblemDetails JSON isn't meant for people.
            if (text && !text.startsWith('{') && !text.startsWith('<')) message = text.replace(/^"|"$/g, '');
        } catch (err) {
            // Keep the generic message.
        }
        throw new GroupApiError(message, response.status);
    }
    return response;
};

const encode = (token: string) => encodeURIComponent(token);

export const groupInvitationService = {
    // Admin
    getPending: async (token: string): Promise<GroupInvitation[]> =>
        (await send('', 'Failed to load invitations', token)).json(),

    invite: async (request: CreateGroupInvitationRequest, token: string): Promise<GroupInvitation> =>
        (await send('', 'Failed to send invitation', token, 'POST', request)).json(),

    resend: async (id: number, token: string): Promise<GroupInvitation> =>
        (await send(`/${id}/resend`, 'Failed to resend invitation', token, 'POST')).json(),

    cancel: async (id: number, token: string): Promise<void> => {
        await send(`/${id}`, 'Failed to cancel invitation', token, 'DELETE');
    },

    // Invitee (any signed-in user; the server checks the invitation is theirs)
    getSetup: async (invitationToken: string, token: string): Promise<GroupInvitationSetup> =>
        (await send(`/token/${encode(invitationToken)}`, 'Failed to load invitation', token)).json(),

    accept: async (
        invitationToken: string,
        request: AcceptGroupInvitationRequest,
        token: string
    ): Promise<AcceptGroupInvitationResponse> =>
        (await send(`/token/${encode(invitationToken)}/accept`, 'Failed to create group', token, 'POST', request)).json(),
};
